import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { Skeleton } from './Skeleton';

export type StatTone = 'default' | 'primary' | 'success' | 'attention';

const TILE: Record<StatTone, string> = {
  default: 'border-line bg-white',
  primary: 'border-primary bg-primary-soft',
  success: 'border-success bg-success-soft',
  attention: 'border-attention-line bg-attention-soft',
};

const TEXT: Record<StatTone, { label: string; value: string }> = {
  default: { label: 'text-muted', value: 'text-ink' },
  primary: { label: 'text-primary', value: 'text-primary' },
  success: { label: 'text-success', value: 'text-success' },
  attention: { label: 'text-attention-ink', value: 'text-attention-ink' },
};

/**
 * One figure with its label: a bill amount, a count on the overview. The value is always the server's;
 * nothing is calculated here. Rendered as a `<dt>`/`<dd>` pair inside a tile, so callers put tiles in a
 * `<dl>`. `state` shows a skeleton while loading or a retry when a read failed.
 */
export function StatTile({
  label,
  value,
  tone = 'default',
  size = 'md',
  state = 'ready',
  onRetry,
  testId,
  className,
}: {
  label: string;
  value: ReactNode;
  tone?: StatTone;
  /** `lg` for the one figure a screen is about, for example the remaining balance. */
  size?: 'md' | 'lg';
  state?: 'ready' | 'pending' | 'error';
  onRetry?: () => void;
  /** `data-testid` on the value, for tests that read the figure. */
  testId?: string;
  className?: string;
}) {
  return (
    <div className={cn('rounded-surface border p-4 shadow-card', TILE[tone], className)}>
      <dt className={cn('text-sm font-medium', TEXT[tone].label)}>{label}</dt>
      <dd
        data-testid={testId}
        className={cn(
          'mt-1 font-bold break-words tabular-nums',
          size === 'lg' ? 'text-3xl leading-9' : 'text-2xl leading-8',
          TEXT[tone].value,
        )}
      >
        {state === 'pending' && <Skeleton className="h-8 w-20" />}
        {state === 'error' && (
          <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-danger">
            Couldn&apos;t load
            {onRetry && (
              <Button variant="tertiary" onClick={onRetry} aria-label={`Try again: ${label}`}>
                Try again
              </Button>
            )}
          </span>
        )}
        {state === 'ready' && value}
      </dd>
    </div>
  );
}
